import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorIcon from '@mui/icons-material/Error';
import GirviPageShell from '../components/common/GirviPageShell';
import baseURL from '../baseURL';

const STATUS_COLOR = {
  completed: 'success',
  pending: 'warning',
  failed: 'error',
  idle: 'default',
  empty: 'default'
};

const ROW_COLOR = {
  Synced: 'success',
  'Pending mirror': 'warning',
  'No data': 'default'
};

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtNum(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-IN');
}

function OverallBanner({ overall }) {
  if (!overall) return null;
  const icon =
    overall.key === 'completed' ? (
      <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
    ) : overall.key === 'pending' ? (
      <HourglassEmptyIcon color="warning" sx={{ fontSize: 40 }} />
    ) : overall.key === 'failed' ? (
      <ErrorIcon color="error" sx={{ fontSize: 40 }} />
    ) : (
      <SyncIcon color="action" sx={{ fontSize: 40 }} />
    );

  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderLeft: 6,
        borderColor: `${STATUS_COLOR[overall.key] || 'grey'}.main`
      }}
    >
      {icon}
      <Box>
        <Typography variant="h6">{overall.label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {overall.detail}
        </Typography>
      </Box>
      <Chip
        label={overall.label}
        color={STATUS_COLOR[overall.key] || 'default'}
        sx={{ ml: 'auto' }}
      />
    </Paper>
  );
}

const SyncStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${baseURL}/sync-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `HTTP ${res.status}`);
      }
      setData(json.data);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to load sync status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const pending = data?.overall?.key === 'pending' || data?.overall?.key === 'failed';
    const live = data?.liveSync || (data?.pledgeSync?.blackGap || 0) > 0;
    const intervalMs = pending || live ? 5000 : 30000;
    const id = setInterval(load, intervalMs);
    return () => clearInterval(id);
  }, [data?.overall?.key, data?.liveSync, data?.pledgeSync?.blackGap, load]);

  const inbound = data?.inbound;

  return (
    <GirviPageShell
      title="VPS A → VPS B Sync Status"
      subtitle="Sync"
      chipLabel="STATUS"
      actions={
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading} sx={{ color: '#f5e6c8' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      }
    >

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {data && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Updated: {fmtTime(data.generatedAt)}
            </Typography>

            <OverallBanner overall={data.overall} />

            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Inbound events (from VPS A)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Chip label={`Success: ${fmtNum(inbound?.success)}`} color="success" />
                <Chip label={`Failed: ${fmtNum(inbound?.failed)}`} color={inbound?.failed ? 'error' : 'default'} />
              </Box>
              {inbound?.lastSuccess && (
                <Typography variant="body2" color="text.secondary">
                  Last success: {inbound.lastSuccess.eventType} at {fmtTime(inbound.lastSuccess.at)}
                </Typography>
              )}
              {inbound?.lastFailed && (
                <Typography variant="body2" color="error">
                  Last failure: {inbound.lastFailed.eventType} — {inbound.lastFailed.error}
                </Typography>
              )}
              {(inbound?.byEventType?.length || 0) > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  By type:{' '}
                  {inbound.byEventType.map((r) => `${r.eventType}: ${r.count}`).join(' · ')}
                </Typography>
              )}
            </Paper>

            {(data.pledgeSync?.legalOnPrimary > 0 || data.pledgeSync?.pledgeCreatedEvents > 0) && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Pledge sync (legal → black side)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Legal on primary: {fmtNum(data.pledgeSync.legalOnPrimary)} · Black on secondary:{' '}
                  {fmtNum(data.pledgeSync.illegalOnSecondary)} · PLEDGE_CREATED events:{' '}
                  {fmtNum(data.pledgeSync.pledgeCreatedEvents)}
                </Typography>
                {data.pledgeSync.legalOnPrimary > 0 && (
                  <LinearProgress
                    variant="determinate"
                    value={data.pledgeSync.progressPct || 0}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                )}
                {data.pledgeSync.blackGap > 0 && (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                    {fmtNum(data.pledgeSync.blackGap)} pledge(s) still syncing to black side…
                  </Typography>
                )}
              </Paper>
            )}

            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Primary vs secondary (girvib_primary / girvib_secondary)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell align="right">Primary</TableCell>
                      <TableCell align="right">Secondary</TableCell>
                      <TableCell align="right">Gap</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.mirror.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{fmtNum(row.primaryCount)}</TableCell>
                        <TableCell align="right">{fmtNum(row.secondaryCount)}</TableCell>
                        <TableCell align="right">{fmtNum(row.gap)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.statusLabel} color={ROW_COLOR[row.statusLabel] || 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {data.overall?.key === 'pending' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Run on VPS B backend: <strong>npm run sync:backfill-secondary</strong>
                </Alert>
              )}
            </Paper>

            {(data.recentFailed?.length || 0) > 0 && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={600} color="error" gutterBottom>
                  Failed events
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Ticket</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recentFailed.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{fmtTime(r.processedAt)}</TableCell>
                          <TableCell>{r.eventType}</TableCell>
                          <TableCell>{r.ticketNo || '—'}</TableCell>
                          <TableCell>
                            <Typography variant="caption">{r.error || '—'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {(data.recentSuccess?.length || 0) > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Recent successful syncs
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Ticket</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recentSuccess.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{fmtTime(r.processedAt)}</TableCell>
                          <TableCell>{r.eventType}</TableCell>
                          <TableCell>{r.ticketNo || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
    </GirviPageShell>
  );
};

export default SyncStatus;
